import React, { useEffect } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { API } from "../lib/api";

const QC_CENTER = [46.4, -72.3]; // between Montréal, Trois-Rivières and Québec

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    // ensure tiles render correctly once the container is visible
    setTimeout(() => map.invalidateSize(), 100);
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 13 });
    } else if (points.length === 1) {
      map.setView(points[0], 13);
    }
  }, [map, points]);
  return null;
};

export const RestaurantMap = ({ restaurants = [], coords = null }) => {
  const navigate = useNavigate();
  const { t } = useLang();

  const withGeo = restaurants.filter(
    (r) => typeof r.lat === "number" && typeof r.lng === "number"
  );

  const center = coords
    ? [coords.lat, coords.lng]
    : withGeo.length > 0
    ? [withGeo[0].lat, withGeo[0].lng]
    : QC_CENTER;
  const zoom = coords ? 12 : 7;

  if (withGeo.length === 0) return null;

  const boundsPoints = [
    ...withGeo.map((r) => [r.lat, r.lng]),
    ...(coords ? [[coords.lat, coords.lng]] : []),
  ];

  return (
    <div data-testid="restaurant-map" className="rounded-2xl overflow-hidden border border-[#E5E0D8] h-[380px] relative z-0">
      <MapContainer
        center={center}
        zoom={zoom}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <FitBounds points={boundsPoints} />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url={`${API}/maptile/{z}/{x}/{y}`}
        />
        {coords && (
          <CircleMarker
            center={[coords.lat, coords.lng]}
            radius={9}
            pathOptions={{ color: "#2563EB", fillColor: "#3B82F6", fillOpacity: 0.9, weight: 2 }}
          >
            <Popup>{t("map.youAreHere")}</Popup>
          </CircleMarker>
        )}
        {withGeo.map((r) => (
          <CircleMarker
            key={r.id}
            center={[r.lat, r.lng]}
            radius={9}
            pathOptions={{ color: "#E63946", fillColor: "#E63946", fillOpacity: 0.85, weight: 2 }}
          >
            <Popup>
              <div className="min-w-[160px]">
                <p className="font-bold text-[#2B2B2B] text-sm">{r.name}</p>
                <p className="text-xs text-[#5C5C5C] font-mono">
                  {r.cuisine}{r.city ? ` · ${r.city}` : ""}
                </p>
                <button
                  data-testid={`map-popup-view-${r.id}`}
                  onClick={() => navigate(`/restaurant/${r.id}`)}
                  className="mt-2 w-full text-center bg-[#E63946] text-white text-xs font-bold px-3 py-1.5 rounded-full hover:opacity-90"
                >
                  {t("map.view")}
                </button>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
};
